import type {
  InviteData,
  LoginResponse,
  Organization,
  OrganizationMember,
  ControllersTransfer,
  Role,
} from '@/types';
import request from '@/utils/request';

export const sendInviteToOrganization = async (
  organizationId: string,
  email: string,
) => {
  const url = `/api/v1/organizations/${organizationId}/invite-user/`;

  return (await request.post(url, { email })).data as { id: string };
};

export const validateInvite = async (token: string) => {
  const url = '/api/v1/organizations-invite/validate/';

  return (await request.post(url, { organizationInviteToken: token }))
    .data as InviteData;
};

export const acceptInvite = async (token: string, password: string): Promise<LoginResponse> => {
  const url = '/api/v1/organizations-invite/accept/';

  return (await request.post(url, { token, password })).data;
};

export const enrollToOrganization = async (token: string) => {
  const url = '/api/v1/organizations-invite/self-enroll/';

  return (await request.post(url, { organizationInviteToken: token }))
    .data as Organization;
};

export const getOrganizations = async () => {
  const url = '/api/v1/organizations/';

  return (await request.get(url)).data as Organization[];
};

export const addOrganization = async (name: string) => {
  const url = '/api/v1/organizations/';

  return (await request.post(url, { name })).data as Organization;
};

export const getOrganization = async (organizationId: string) => {
  const url = `/api/v1/organizations/${organizationId}/`;

  return (await request.get(url)).data as Organization;
};

export const updateOrganization = async ({
  organizationId,
  name,
  totpRequired,
}: {
  organizationId: string;
  name?: string;
  totpRequired?: boolean;
}) => {
  const url = `/api/v1/organizations/${organizationId}/`;

  return (await request.patch(url, { name, totpRequired }))
    .data as Organization;
};

export const leaveOrganization = async (organizationId: string) => {
  const url = `/api/v1/organizations/${organizationId}/leave/`;

  await request.post(url);
};

export const deleteOrganization = async (organizationId: string): Promise<void> => {
  await request.delete(`/api/v1/organizations/${organizationId}/`);
};

export const getMembers = async (organizationId: string) => {
  const url = `/api/v1/organizations/${organizationId}/members/`;

  return (await request.get(url)).data as OrganizationMember[];
};

export const removeMember = async ({
  organizationId,
  userId,
}: {
  organizationId: string;
  userId: string;
}) => {
  const url = `/api/v1/organizations/${organizationId}/members/${userId}/`;

  await request.delete(url);
};

export const changeRole = async ({
  organizationId,
  userId,
  role,
}: {
  organizationId: string;
  userId: string;
  role: Role;
}) => {
  const url = `/api/v1/organizations/${organizationId}/members/${userId}/`;

  await request.put(url, { role });
};

export const checkGrafanaAccess = async (organizationId: string) => {
  const url = `/api/v1/organizations/${organizationId}/grafana-access/`;

  return (await request.get(url)).data.isGrafanaReady as boolean | null;
};

export const transferControllersInternal = async (
  organizationId: string,
  targetOrganizationId: string,
  controllers: string[],
  clearControllersInfo: boolean,
) => {
  const url = `/api/v1/organizations/${organizationId}/controllers-transfer/`;

  return (await request.post(url, { controllers, targetOrganizationId, clearControllersInfo })).data;
};

export const transferControllersExternal = async (
  organizationId: string,
  targetEmail: string,
  controllers: string[],
  clearControllersInfo: boolean,
) => {
  const url = `/api/v1/organizations/${organizationId}/controllers-transfer-requests/`;

  return (await request.post(url, { controllers, targetEmail, clearControllersInfo })).data;
};

export const getExternalTransfer = async (
  organizationId: string,
  transferRequestId: string,
): Promise<ControllersTransfer> => {
  const url = `/api/v1/organizations/${organizationId}/controllers-transfer-requests/${transferRequestId}/`;

  return (await request.get(url)).data;
};

export const cancelControllersTransfer = async (organizationId: string, transferRequestId: string) => {
  const url = `/api/v1/organizations/${organizationId}/controllers-transfer-requests/${transferRequestId}/`;

  return (await request.delete(url)).data;
};

export const validateControllersTransfer = async (token: string) => {
  const url = '/api/v1/controllers-transfer-request/validate/';

  return (await request.post(url, { token })).data;
};

export const acceptControllersTransfer = async (token: string, targetOrganizationId: string) => {
  const url = '/api/v1/controllers-transfer-request/accept/';

  return (await request.post(url, { token, targetOrganizationId })).data;
};
